import { Multiselect, SearchBar, SelectItem } from '@hubtype/ui-react-web'
import { useState } from 'react'

import {
  ContentType,
  TopContentFields,
} from '../../../../domain/models/content-fields'
import { TextBodySmall } from '../../base'
import { StyledHeader } from '../../flow-selector/flow-selector-styles'
import { InputAction } from '../constants'
import { ConditionalContentTitle, FullWidthContainer } from '../editor-styles'
import { EditorProps } from '../node-editor-panel'
import { SortableContentWidget } from './sortable-widget/sortable-content-widget'

interface SortableDropdownWidgetProps<T extends SelectItem>
  extends EditorProps {
  nodeContent: TopContentFields
  contentType: ContentType
  applyButtonText: string
  field: InputAction
  values: T[]
  availableItems: T[]
  sortableValues?: T[]
  isDragDisabled?: boolean
  searchPlaceholder?: string
  isLabelHidden?: boolean
  header?: (item: T, index: number) => React.ReactNode
}

export const SortableDropdownWidgetField = <T extends SelectItem>(
  props: SortableDropdownWidgetProps<T>
): JSX.Element => {
  const [filterValue, setFilterValue] = useState('')
  const onApply = (selectedKeys: string[]) => {
    const newValues = selectedKeys
      .map(key => props.availableItems.find(item => item.id === key))
      .filter(Boolean)

    if (props.isDragDisabled) {
      newValues.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
    }
    props.onChange({
      type: props.field.actionType,
      fieldKey: props.field.key,
      value: newValues,
      edges: getEdges(selectedKeys),
    })
  }

  const getEdges = (selectedKeys: string[]) => {
    const invalidEdges = props.values
      .filter(item => !selectedKeys.includes(item.id))
      .map(item => `${props.nodeContent.id}-${item.id}`)

    return props.nodeContent.edges.filter(
      edge => edge.sourceHandle && !invalidEdges.includes(edge.sourceHandle)
    )
  }

  const handleDelete = (item: SelectItem) => {
    props.onChange({
      type: props.field.actionType,
      fieldKey: props.field.key,
      value: (props.sortableValues || props.values).filter(
        v => v.id !== item.id
      ),
      edges: props.nodeContent.edges.filter(
        edge => edge.sourceHandle !== `${props.nodeContent.id}-${item.id}`
      ),
    })
  }

  return (
    <FullWidthContainer>
      <Multiselect
        defaultSelectedKeys={props.values.map(v => v.id)}
        applyButtonText={props.applyButtonText}
        onApply={onApply}
        items={props.availableItems}
        label={props.field.label}
        placeholder={props.field.placeholder}
        isReadOnly={props.isReadOnly}
        filterValue={filterValue}
        isMissing={props.showMissingFields && props.values.length === 0}
        isLabelHidden={props.isLabelHidden}
        header={
          props.searchPlaceholder &&
          props.availableItems.length > 10 && (
            <StyledHeader>
              <SearchBar
                label={props.searchPlaceholder}
                defaultValue={filterValue}
                size='small'
                onChangeWithDebounce={setFilterValue}
              />
            </StyledHeader>
          )
        }
      />
      {(props.values.length > 0 || (props.sortableValues?.length ?? 0) > 0) && (
        <SortableContentWidget
          {...props}
          nodeId={props.nodeContent.id}
          sortableContents={props.sortableValues || props.values}
          isRemovable={true}
          onRemove={handleDelete}
          header={(item, index) => (
            <ConditionalContentTitle
              key={item.id}
              $disableGripDots={props.isDragDisabled}
            >
              {props.header?.(item, index) || (
                <TextBodySmall>{item.name}</TextBodySmall>
              )}
            </ConditionalContentTitle>
          )}
        ></SortableContentWidget>
      )}
    </FullWidthContainer>
  )
}
